import { useRef, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiDownload,
  mdiFileImportOutline,
  mdiTrashCanOutline,
} from "@mdi/js";
import Papa from "papaparse";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  ModalType,
  useModal,
} from "../../../../context_providers/modal/modal_context";
import {
  FetchDevicesByScanCodesApi,
  ScanCodeDevice,
} from "./bulk_stock_upload_repo";

const PREVIEW_LIMIT = 10;

export default function ScanCodeDevicesFetchForm(): JSX.Element {
  const { showToast } = useToast();
  const { showModal } = useModal();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [devices, setDevices] = useState<ScanCodeDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const downloadSampleCSV = () => {
    const csvContent = "Scan ID\n12345678";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "scan_code_devices_sample.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setDevices([]);
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
  };

  const clearFile = () => {
    setFile(null);
    setDevices([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const showScanIdsModal = (
    title: string,
    message: string,
    ids: string[]
  ) => {
    showModal({
      title,
      type: ModalType.modal,
      container: () => (
        <div id="scan_ids_modal" className="p-4 space-y-3 max-w-md">
          <p className="text-sm text-gray-600">{message}</p>
          <div className="max-h-72 overflow-auto border rounded-md divide-y divide-gray-100">
            {ids.map((id, index) => (
              <div
                id={`scan_id_row_${id}`}
                key={index}
                className="px-4 py-2 text-sm font-mono text-gray-800"
              >
                {id}
              </div>
            ))}
          </div>
        </div>
      ),
    });
  };

  const downloadDevicesCSV = () => {
    if (devices.length === 0) return;
    const rows = devices.map((d) => ({
      "Scan ID": d.id ?? "",
      "Device ID": d.device_id ?? "",
      "Item Code": d.item_code ?? "",
      "Box Name": d.box_name ?? "",
    }));
    const csvContent = Papa.unparse(rows);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "scan_code_devices.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFetch = async () => {
    if (!file) return setError("Please upload a file with scan codes");

    setError("");

    try {
      setLoading(true);
      const res = await FetchDevicesByScanCodesApi(file);

      if (!res || res.success !== true) {
        const invalid: string[] = res?.invalidScanIds || [];
        if (invalid.length > 0) {
          showScanIdsModal(
            `Invalid Scan IDs (${invalid.length})`,
            res?.error || "Some Scan IDs exceed 8 digits.",
            invalid
          );
        } else {
          showToast({
            type: ToastType.error,
            text: res?.error || res?.message || "Failed to fetch devices",
          });
        }
        return;
      }

      const list: ScanCodeDevice[] = res?.data?.devices || [];
      setDevices(Array.isArray(list) ? list : []);

      if (!list || list.length === 0) {
        showToast({
          type: ToastType.warning,
          text: "No devices found for the uploaded scan codes",
        });
      } else {
        showToast({
          type: ToastType.success,
          text: `${list.length} device(s) fetched`,
        });
      }
      console.log("result:",res?.data)

      const notFound: string[] = res?.data?.notFoundScanIds || [];
      if (notFound.length > 0) {
        console.log("result data:", notFound);
        showScanIdsModal(
          `Scan IDs Not Found (${notFound.length})`,
          "The following scan ID(s) had no matching devices:",
          notFound
        );
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="scan_form_container">
      <div className="p-6 space-y-6">
        {/* File Upload */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Upload Scan Codes</h2>
            <button
              id="download_sample_btn"
              onClick={downloadSampleCSV}
              className="text-gray-500 text-xs flex items-center gap-1 hover:text-blue-600 underline"
            >
              <Icon path={mdiDownload} size={0.5} />
              Download Sample CSV
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Upload a CSV with a single <span className="font-medium">Scan
            ID</span> column containing 8-digit codes.
          </p>

          <div className="relative">
            <input
              id="csv_file_input"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={loading}
            />
            <button
              id="upload_btn"
              disabled={loading}
              className="w-full border-2 border-dashed border-blue-400 text-blue-600 h-24 rounded-md font-medium flex flex-col items-center justify-center gap-2 hover:bg-blue-50 transition-all disabled:opacity-50"
            >
              <Icon path={mdiFileImportOutline} size={1} />
              {file ? file.name : "Click to upload CSV (Scan ID)"}
            </button>
          </div>

          {file && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-green-700">
                {file.name} ready
              </p>
              <button
                id="clear_file_btn"
                onClick={clearFile}
                disabled={loading}
                className="text-red-600 flex items-center gap-1 text-xs hover:text-red-700 disabled:opacity-50"
              >
                <Icon path={mdiTrashCanOutline} size={0.6} />
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Device list */}
        {devices.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">
                Devices ({devices.length})
                {devices.length > PREVIEW_LIMIT && (
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    showing first {PREVIEW_LIMIT}
                  </span>
                )}
              </h3>
              <button
                id="download_devices_btn"
                onClick={downloadDevicesCSV}
                className="text-blue-600 text-xs flex items-center gap-1 hover:text-blue-700 underline"
              >
                <Icon path={mdiDownload} size={0.6} />
                Download CSV
              </button>
            </div>
            <div id="devices_table" className="max-h-80 overflow-auto border rounded-md">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">#</th>
                    <th className="px-4 py-2 text-left font-medium">
                      Scan ID
                    </th>
                    <th className="px-4 py-2 text-left font-medium">
                      Device ID
                    </th>
                    <th className="px-4 py-2 text-left font-medium">
                      Item Code
                    </th>
                    <th className="px-4 py-2 text-left font-medium">
                      Box Name
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {devices.slice(0, PREVIEW_LIMIT).map((device, index) => (
                    <tr id={`device_row_${device?.id ?? index}`} key={index}>
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">{device?.id ?? "-"}</td>
                      <td className="px-4 py-2">{device?.device_id ?? "-"}</td>
                      <td className="px-4 py-2">{device?.item_code ?? "-"}</td>
                      <td className="px-4 py-2">{device?.box_name ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
            {error}
          </p>
        )}
      </div>

      <div className="border-t px-6 py-4 flex justify-end">
        <button
          id="fetch_btn"
          onClick={handleFetch}
          disabled={loading || !file}
          className={`bg-blue-600 text-white px-6 py-2 rounded-lg transition-all ${
            loading || !file
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-blue-700"
          }`}
        >
          {loading ? "Fetching..." : "Fetch Devices"}
        </button>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
